import {Component, model, OnDestroy} from '@angular/core';
import {Subscription, of} from "rxjs";
import {catchError, tap, finalize} from "rxjs/operators";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {AlertService} from "../../../../common/alert/alert.service";
import {AlertComponent} from "../../../../common/alert/alert.component";
import {NgForOf, NgIf} from "@angular/common";
import {PinholePromoCodeService, PromoCodeCategory, PromoCodeTypes} from "../../services/pinhole.promo-code.service";

@Component({
    selector: 'generate',
    templateUrl: './generate.component.html',
    imports: [
        ReactiveFormsModule,
        AlertComponent,
        NgForOf,
        NgIf
    ],
    styleUrls: ['./generate.component.css'],
    providers: [AlertService]
})

export class GenerateComponent implements OnDestroy {
    PromoCodeScope = PromoCodeScope;

    private generatePromoCodesBuilder = new FormBuilder().nonNullable

    private promoCodeScope = this.generatePromoCodesBuilder.control(PromoCodeScope.Private)
    private category = this.generatePromoCodesBuilder.control("")
    private batch = this.generatePromoCodesBuilder.control("")
    private description = this.generatePromoCodesBuilder.control("")
    private type = this.generatePromoCodesBuilder.control("")
    private expiration = this.generatePromoCodesBuilder.control(null)
    private count = this.generatePromoCodesBuilder.control(0)
    private code = this.generatePromoCodesBuilder.control("")
    private subscriptions: Subscription[] = [];

    private privateOnlyFields = [this.batch, this.count]
    private publicOnlyFields = [this.code]

    isLoading: boolean = false;
    generatePromoCodesForm = new FormGroup({
        promoCodeScope: this.promoCodeScope,
        code: this.code,
        category: this.category,
        type: this.type,
        batch: this.batch,
        description: this.description,
        expiration: this.expiration,
        count: this.count,
    });

    readonly promoCodes = model<string[] | null>(null);
    readonly promoCodeCategories = model<PromoCodeCategory | null>(null);
    readonly promoCodeTypes = model<PromoCodeTypes | null>(null);
    readonly scopes = [PromoCodeScope.Private, PromoCodeScope.Public]

    constructor(private service: PinholePromoCodeService, private alertService: AlertService) {
        this.loadTypes();
        this.loadCategories();

        this.subscriptions.push(
            this.promoCodeScope.valueChanges.subscribe(value =>
                this.onScopeChange(value)
            )
        )
        this.onScopeChange(this.promoCodeScope.value)
    }

    onScopeChange(value: PromoCodeScope) {
        this.privateOnlyFields.forEach(control => {
            if (value == PromoCodeScope.Private) {
                control.setValidators([Validators.required]);
            } else {
                control.clearValidators();
            }
            control.updateValueAndValidity();
        })
        this.publicOnlyFields.forEach(control => {
            if (value == PromoCodeScope.Public) {
                control.setValidators([Validators.required]);
            } else {
                control.clearValidators();
            }
            control.updateValueAndValidity();
        })
    }

    currentScope() : PromoCodeScope {
        return this.promoCodeScope.value
    }


    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe())
    }

    loadTypes() {
        this.alertService.clearAlerts();
        this.subscriptions.push(
            this.service
                .getTypes()
                .subscribe(value => {
                    if (value != null) {
                        this.promoCodeTypes.set(value);
                    }
                })
        );
    }

    loadCategories() {
        this.alertService.clearAlerts();
        this.subscriptions.push(
            this.service
                .getCategories()
                .subscribe(value => {
                    if (value != null) {
                        this.promoCodeCategories.set(value);
                    }
                })
        );
    }

    submit() {
        this.alertService.clearAlerts();
        this.isLoading = true;
        this.promoCodes.set(null);
        if (this.promoCodeScope.value == PromoCodeScope.Private) {
            this.generatePrivate();
        } else {
            this.createPublic();
        }
    }

    generatePrivate() {
        this.subscriptions.push(
            this.service
                .generatePrivateCodes({
                        category: this.category.value,
                        batch: this.batch.value,
                        description: this.description.value,
                        type: this.type.value,
                        expiration: this.expiration.value? new Date(this.expiration.value).toISOString() : null,
                        count: this.count.value,
                    }
                )
                .subscribe(value => {
                    this.isLoading = false;
                    if (value != null) {
                        this.promoCodes.set(value);
                    } else {
                        this.alertService.showAlert('danger', "Server Error");
                    }
                })
        );
    }

    createPublic() {
        this.subscriptions.push(
            this.service
                .createPublicCode({
                    code: this.code.value.toUpperCase(),
                    category: this.category.value,
                    description: this.description.value,
                    type: this.type.value,
                    expiration: this.expiration.value? new Date(this.expiration.value).toISOString() : null,
                })
                .pipe(
                    tap(() => this.alertService.showAlert('success', "Promo code created successfully")),
                    catchError(() => {
                        this.alertService.showAlert('danger', "Failed to create promo code")
                        return of(null)
                    }),
                    finalize(() => this.isLoading = false)
                )
                .subscribe()
        );
    }
}

export enum PromoCodeScope {
    Public = "Public",
    Private = "Private"
}